import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, ShoppingCart, Tag, X, Loader2, AlertCircle, Crown, Zap } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'fruitCrush_token';

// Rarity colors
const RARITY_COLORS = {
  common: 'border-gray-400 bg-gray-500/10',
  rare: 'border-blue-400 bg-blue-500/10',
  epic: 'border-purple-400 bg-purple-500/10',
  legendary: 'border-yellow-400 bg-yellow-500/10'
};

export function GrandExchangeModal({ isOpen, onClose, userCoins, onCoinsChange, userInventory, userEquipment, userCollectedFruits, userCosmetics, onPurchase }) {
  const [activeTab, setActiveTab] = useState('rare');
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [tradeableItems, setTradeableItems] = useState([]);
  const [systemStock, setSystemStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buyingItem, setBuyingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const modalRef = useRef(null);
  
  // Sell form state
  const [selectedItem, setSelectedItem] = useState(null);
  const [sellQuantity, setSellQuantity] = useState(1);
  const [sellPrice, setSellPrice] = useState(10);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position and lock body
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore scroll position when modal closes
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const getAuthHeader = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? `Bearer ${token}` : '';
  };

  // Fetch system stock (rare items like God Sword)
  const fetchSystemStock = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/exchange/system-stock`);
      if (response.ok) {
        const data = await response.json();
        setSystemStock(data.items);
      }
    } catch (error) {
      console.error('Failed to fetch system stock:', error);
    }
  }, []);

  // Fetch tradeable items
  const fetchTradeableItems = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/exchange/items`);
      if (response.ok) {
        const data = await response.json();
        setTradeableItems(data.items);
      }
    } catch (error) {
      console.error('Failed to fetch tradeable items:', error);
    }
  }, []);

  // Fetch listings
  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/exchange/listings?limit=50`;
      if (filterType !== 'all') {
        url += `&item_type=${filterType}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setListings(data.listings);
      }
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    }
    setLoading(false);
  }, [filterType, searchQuery]);

  // Fetch my listings
  const fetchMyListings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/exchange/my-listings`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        const data = await response.json();
        setMyListings(data.listings);
      }
    } catch (error) {
      console.error('Failed to fetch my listings:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchTradeableItems();
      fetchListings();
      fetchMyListings();
      fetchSystemStock();
    }
  }, [isOpen, fetchTradeableItems, fetchListings, fetchMyListings, fetchSystemStock]);

  // Buy a system stock item (like God Sword)
  const handleBuySystemStock = async (item) => {
    if (userCoins < item.price) {
      toast.error(`Not enough coins! Need ${item.price.toLocaleString()}, have ${userCoins.toLocaleString()}`);
      return;
    }

    // Check if user already owns it
    if (userEquipment?.includes(item.id)) {
      toast.error('You already own this item!');
      return;
    }

    setBuyingItem(item.id);
    try {
      const response = await fetch(`${API_URL}/api/exchange/system-stock/buy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader()
        },
        body: JSON.stringify({ item_id: item.id })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(`🎉 ${data.message}`);
        onCoinsChange?.(-item.price);
        onPurchase?.();
        fetchSystemStock();
      } else {
        toast.error(data.detail || 'Failed to purchase');
      }
    } catch (error) {
      toast.error('Failed to purchase item');
    }
    setBuyingItem(null);
  };

  // Buy an item
  const handleBuy = async (listing) => {
    if (userCoins < listing.total_price) {
      toast.error(`Not enough coins! Need ${listing.total_price}, have ${userCoins}`);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/exchange/buy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader()
        },
        body: JSON.stringify({
          listing_id: listing.id,
          quantity: listing.quantity
        })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        onCoinsChange?.(-listing.total_price);
        fetchListings();
      } else {
        toast.error(data.detail || 'Failed to purchase');
      }
    } catch (error) {
      toast.error('Failed to purchase item');
    }
  };

  // Create a listing
  const handleCreateListing = async () => {
    if (!selectedItem) {
      toast.error('Select an item to sell');
      return;
    }
    if (sellPrice < 1) {
      toast.error('Price must be at least 1 coin');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/exchange/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader()
        },
        body: JSON.stringify({
          item_id: selectedItem.id,
          item_type: selectedItem.type,
          quantity: sellQuantity,
          price_per_unit: sellPrice
        })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success('Item listed successfully!');
        setSelectedItem(null);
        setSellQuantity(1);
        setSellPrice(10);
        fetchMyListings();
        fetchListings();
      } else {
        toast.error(data.detail || 'Failed to create listing');
      }
    } catch (error) {
      toast.error('Failed to create listing');
    }
  };

  // Cancel a listing
  const handleCancelListing = async (listingId) => {
    try {
      const response = await fetch(`${API_URL}/api/exchange/cancel/${listingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': getAuthHeader() }
      });

      const data = await response.json();
      if (response.ok) {
        toast.success('Listing cancelled');
        fetchMyListings();
      } else {
        toast.error(data.detail || 'Failed to cancel');
      }
    } catch (error) {
      toast.error('Failed to cancel listing');
    }
  };

  // Get user's available items to sell
  const getAvailableItems = () => {
    const items = [];
    
    // Add consumables from inventory
    if (userInventory) {
      Object.entries(userInventory).forEach(([itemId, qty]) => {
        if (qty > 0) {
          const itemInfo = tradeableItems.find(i => i.id === itemId);
          if (itemInfo) {
            items.push({ ...itemInfo, quantity: qty, source: 'inventory' });
          }
        }
      });
    }
    
    // Add collected fruits
    if (userCollectedFruits) {
      Object.entries(userCollectedFruits).forEach(([itemId, qty]) => {
        if (qty > 0) {
          const itemInfo = tradeableItems.find(i => i.id === itemId);
          if (itemInfo) {
            items.push({ ...itemInfo, quantity: qty, source: 'collected_fruits' });
          }
        }
      });
    }
    
    // Add equipment
    if (userEquipment && userEquipment.length > 0) {
      const equipmentCount = {};
      userEquipment.forEach(itemId => {
        equipmentCount[itemId] = (equipmentCount[itemId] || 0) + 1;
      });
      
      Object.entries(equipmentCount).forEach(([itemId, qty]) => {
        const itemInfo = tradeableItems.find(i => i.id === itemId);
        if (itemInfo) {
          items.push({ ...itemInfo, quantity: qty, source: 'equipment' });
        }
      });
    }
    
    // Add cosmetics (skins, effects, themes, frames)
    if (userCosmetics) {
      const cosmeticTypes = ['unlocked_skins', 'unlocked_effects', 'unlocked_themes', 'unlocked_frames'];
      cosmeticTypes.forEach(cosmeticType => {
        const unlockedItems = userCosmetics[cosmeticType] || [];
        unlockedItems.forEach(itemId => {
          const itemInfo = tradeableItems.find(i => i.id === itemId);
          if (itemInfo) {
            items.push({ ...itemInfo, quantity: 1, source: 'cosmetic', cosmeticType });
          }
        });
      });
    }
    
    return items;
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      style={{ touchAction: 'none' }}
      onClick={(e) => {
        // Close modal when clicking backdrop
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div 
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col"
        style={{ 
          maxHeight: '85vh',
          touchAction: 'pan-y', 
          overscrollBehavior: 'contain' 
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-2xl">🏛️</span>
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-white">Grand Exchange</h2>
                <p className="text-xs text-white/70">Trade with other players</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-white/20 rounded-full px-3 py-1">
                <span>🪙</span>
                <span className="font-bold text-white">{userCoins}</span>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b bg-gray-50">
          {[
            { id: 'rare', label: 'Rare Items', icon: '👑' },
            { id: 'browse', label: 'Browse', icon: '🛒' },
            { id: 'sell', label: 'Sell', icon: '💰' },
            { id: 'my-listings', label: 'My Listings', icon: '📋' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-2 font-semibold text-xs transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-purple-700 border-b-2 border-purple-500'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div 
          className="flex-1 overflow-y-auto"
          style={{ 
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            minHeight: 0
          }}
        >
          {/* Rare Items Tab (System Stock) */}
          {activeTab === 'rare' && (
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3 p-2 bg-gradient-to-r from-yellow-100 to-amber-100 rounded-lg border border-yellow-300">
                <Crown className="w-5 h-5 text-yellow-600" />
                <div>
                  <p className="text-sm font-bold text-yellow-800">Legendary Items</p>
                  <p className="text-xs text-yellow-700">Limited stock - once they're gone, they're gone!</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {systemStock.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    <p>Loading rare items...</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {systemStock.map(item => {
                      const isOwned = userEquipment?.includes(item.id);
                      const isSoldOut = item.remaining_stock <= 0;
                      const canAfford = userCoins >= item.price;
                      
                      return (
                        <div
                          key={item.id}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            item.rarity === 'legendary' 
                              ? 'border-yellow-400 bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50' 
                              : RARITY_COLORS[item.rarity || 'common']
                          } ${isSoldOut ? 'opacity-60' : ''}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg">
                              <span className="text-3xl">{item.emoji}</span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-bold text-lg">{item.name}</h3>
                                {item.rarity === 'legendary' && (
                                  <span className="text-[10px] px-2 py-0.5 bg-gradient-to-r from-yellow-400 to-amber-500 text-white rounded-full font-bold">
                                    LEGENDARY
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mt-0.5">{item.description}</p>
                              
                              {/* Features */}
                              {item.uses && (
                                <div className="flex items-center gap-1 mt-2 text-xs text-purple-600">
                                  <Zap className="w-3 h-3" />
                                  <span>{item.uses} uses • {item.recharge_hours}h recharge</span>
                                </div>
                              )}
                              
                              {/* Stock indicator */}
                              <div className="mt-2">
                                <div className="flex items-center justify-between text-xs mb-1">
                                  <span className="text-gray-500">Stock remaining:</span>
                                  <span className={`font-bold ${item.remaining_stock <= 2 ? 'text-red-500' : 'text-green-600'}`}>
                                    {item.remaining_stock} / {item.max_stock}
                                  </span>
                                </div>
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full transition-all ${
                                      item.remaining_stock <= 2 ? 'bg-red-500' : 'bg-green-500'
                                    }`}
                                    style={{ width: `${(item.remaining_stock / item.max_stock) * 100}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Price & Buy */}
                          <div className="flex items-center justify-between mt-4 pt-3 border-t border-yellow-200">
                            <div className="text-xl font-bold text-amber-600">
                              🪙 {item.price.toLocaleString()}
                            </div>
                            {isOwned ? (
                              <Button disabled className="bg-gray-400">
                                ✓ Owned
                              </Button>
                            ) : isSoldOut ? (
                              <Button disabled className="bg-gray-400">
                                Sold Out
                              </Button>
                            ) : (
                              <Button
                                onClick={() => handleBuySystemStock(item)}
                                disabled={!canAfford || buyingItem === item.id}
                                className={`${
                                  canAfford 
                                    ? 'bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600' 
                                    : 'bg-gray-400'
                                }`}
                              >
                                {buyingItem === item.id ? (
                                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Buying...</>
                                ) : canAfford ? (
                                  <>Buy Now</>
                                ) : (
                                  <>Not enough coins</>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Browse Tab */}
          {activeTab === 'browse' && (
            <div className="p-4">
              {/* Search & Filter */}
              <div className="flex gap-2 mb-3">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 h-9"
                  />
                </div>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="h-9 px-2 rounded-md border text-sm"
                >
                  <option value="all">All</option>
                  <option value="consumable">Consumables</option>
                  <option value="equipment">Equipment</option>
                  <option value="cosmetic">Cosmetics</option>
                </select>
              </div>

              {/* Listings */}
              <div className="space-y-2">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                  </div>
                ) : listings.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No listings found</p>
                    <p className="text-xs mt-1">Be the first to list an item!</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {listings.map(listing => {
                      const itemInfo = tradeableItems.find(i => i.id === listing.item_id);
                      const rarity = itemInfo?.rarity || 'common';
                      
                      // Calculate average price for this item type
                      const sameItemListings = listings.filter(l => l.item_id === listing.item_id);
                      const avgPrice = sameItemListings.length > 0 
                        ? Math.round(sameItemListings.reduce((sum, l) => sum + (l.total_price / l.quantity), 0) / sameItemListings.length)
                        : listing.total_price / listing.quantity;
                      const pricePerUnit = Math.round(listing.total_price / listing.quantity);
                      const priceDiff = pricePerUnit - avgPrice;
                      const priceIndicator = priceDiff > avgPrice * 0.2 ? 'high' : priceDiff < -avgPrice * 0.2 ? 'low' : 'fair';
                      
                      return (
                        <div
                          key={listing.id}
                          className={`p-3 rounded-lg border-2 ${RARITY_COLORS[rarity]}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{listing.item_emoji}</span>
                              <div>
                                <p className="font-semibold text-sm">{listing.item_name}</p>
                                <p className="text-xs text-gray-500">
                                  by {listing.seller_name} • x{listing.quantity}
                                </p>
                                {/* Average price indicator */}
                                <p className={`text-xs mt-0.5 ${
                                  priceIndicator === 'high' ? 'text-red-500' : 
                                  priceIndicator === 'low' ? 'text-green-500' : 
                                  'text-gray-400'
                                }`}>
                                  {priceIndicator === 'high' && '📈 Above avg'}
                                  {priceIndicator === 'low' && '📉 Below avg'}
                                  {priceIndicator === 'fair' && '📊 Fair price'}
                                  {' '}(avg: {avgPrice}/ea)
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-amber-600">🪙 {listing.total_price}</p>
                              <p className="text-xs text-gray-400">{pricePerUnit}/ea</p>
                              <Button
                                size="sm"
                                onClick={() => handleBuy(listing)}
                                disabled={userCoins < listing.total_price}
                                className="mt-1 h-7 text-xs bg-green-500 hover:bg-green-600"
                              >
                                Buy
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sell Tab */}
          {activeTab === 'sell' && (
            <div className="p-4">
              <p className="text-sm text-gray-500 mb-2">Select an item from your inventory to sell:</p>
              
              <div className="grid grid-cols-2 gap-2 mb-3">
                  {getAvailableItems().map((item, index) => (
                    <button
                      key={`${item.id}-${item.source}-${index}`}
                      onClick={() => {
                        setSelectedItem(item);
                        setSellQuantity(1);
                      }}
                      className={`p-2 rounded-lg border-2 text-left transition-all ${
                        selectedItem?.id === item.id && selectedItem?.source === item.source
                          ? 'border-purple-400 bg-purple-50'
                          : `${RARITY_COLORS[item.rarity || 'common']} hover:border-purple-300`
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{item.emoji}</span>
                        <div>
                          <p className="font-semibold text-xs">{item.name}</p>
                          <p className="text-[10px] text-gray-500">
                            {item.source === 'cosmetic' ? '✨ Cosmetic' : `x${item.quantity}`}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                  {getAvailableItems().length === 0 && (
                    <div className="col-span-2 text-center py-8 text-gray-500">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No items to sell</p>
                    </div>
                  )}
              </div>

              {/* Sell Form */}
              {selectedItem && (
                <div className="flex-shrink-0 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{selectedItem.emoji}</span>
                    <div>
                      <p className="font-bold text-sm">{selectedItem.name}</p>
                      <p className="text-xs text-gray-500">Available: {selectedItem.quantity}</p>
                    </div>
                    <button 
                      onClick={() => setSelectedItem(null)}
                      className="ml-auto p-1 rounded hover:bg-purple-100"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className="text-xs text-gray-600">Quantity</label>
                      <Input
                        type="number"
                        min={1}
                        max={selectedItem.quantity}
                        value={sellQuantity}
                        onChange={(e) => setSellQuantity(Math.min(parseInt(e.target.value) || 1, selectedItem.quantity))}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Price (per unit)</label>
                      <Input
                        type="number"
                        min={1}
                        value={sellPrice}
                        onChange={(e) => setSellPrice(parseInt(e.target.value) || 1)}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <p className="text-sm">
                      Total: <span className="font-bold text-purple-600">🪙 {sellQuantity * sellPrice}</span>
                    </p>
                    <Button onClick={handleCreateListing} size="sm" className="bg-purple-500 hover:bg-purple-600">
                      <Tag className="w-4 h-4 mr-1" /> List for Sale
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* My Listings Tab */}
          {activeTab === 'my-listings' && (
            <div className="p-4">
              {myListings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Tag className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No active listings</p>
                  <p className="text-xs mt-1">List items in the Sell tab</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {myListings.map(listing => (
                      <div
                        key={listing.id}
                        className="p-3 rounded-lg border bg-white"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">{listing.item_emoji}</span>
                            <div>
                              <p className="font-semibold text-sm">{listing.item_name}</p>
                              <p className="text-xs text-gray-500">x{listing.quantity}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-amber-600">🪙 {listing.total_price}</p>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleCancelListing(listing.id)}
                              className="mt-1 h-7 text-xs"
                            >
                              <X className="w-3 h-3 mr-1" /> Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t bg-gray-50 text-center text-xs text-gray-500">
          💡 Trade items with other players using coins
        </div>
      </div>
    </div>
  );
}
